import React, { useState } from 'react';
import {
  Brain,
  Sparkles,
  GraduationCap,
  ArrowRight,
  Check,
  Building2,
  Briefcase,
  Users,
  Loader2,
  ShieldCheck,
  ShoppingCart,
  X,

  Star,
  Crown,
  Gem,
} from 'lucide-react';
import { Link } from 'react-router-dom';

import API_BASE from '../config';

// Programme + Price mapping (UPDATED as per details)
const PROGRAMME_PRICING = {
  // General programmes
  'advance-growth-mastery': {
    label: 'Advance Growth Mastery',
    price: 15000,
    originalPrice: 15000,
  },
  'scientific-visualization-daily': {
    label: 'Creative Manifestation Practice',
    price: 1000,
    originalPrice: 1000,
  },
  'limitless-mind-rewiring': {
    label: 'Limitless Mind Rewiring Programme',
    price: 4999,
    originalPrice: 4999,
  },
  'student-dream-mastery': {
    label: 'Career Dream Mastery',
    price: 999,
    originalPrice: 999,
  },
  'identity-transformation-lab': {
    label: 'Growth Mastery',
    price: 999,
    originalPrice: 999,
  },

  // Advisor Category – 3 online programmes
  'special-growth-mastery': {
    label: 'Special Growth Mastery',
    price: 4999,
    originalPrice: 4999,
  },
  'elite-growth-mastery': {
    label: 'Elite Growth Mastery',
    price: 24999,
    originalPrice: 24999,
  },
  'legacy-growth-mastery': {
    label: 'Legacy Growth Mastery (12 Months)',
    price: 99999,
    originalPrice: 99999,
  },

  // Leadership Category – 2 programmes
  'leadership-growth-mastery': {
    label: 'High-Performance Leadership',
    price: 4999,
    originalPrice: 4999,
  },
  'advance-leadership-transformation-mastery': {
    label: 'Advance Leadership Transformation Mastery',
    price: 24999,
    originalPrice: 24999,
  },
  'leadership-legacy-program': {
    label: 'Leadership Legacy Program',
    price: 99999,
    originalPrice: 99999,
  },
};

const ProgrammeCategories = () => {
  const [selectedPlan, setSelectedPlan] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    notes: '',
    consent: false,
  });

  const [submitting, setSubmitting] = useState(false);
  const [proposalSent, setProposalSent] = useState(false);

  const handleEnrollClick = (plan) => {
    setSelectedPlan(plan);

    setTimeout(() => {
      document
        .getElementById('programme-checkout')
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    let newValue = type === 'checkbox' ? checked : value;

    // Enforce 10-digit numeric only for phone
    if (name === 'phone') {
      newValue = value.replace(/[^0-9]/g, '').slice(0, 10);
    }

    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedPlan) return;

    const programme = PROGRAMME_PRICING[selectedPlan];

    if (!formData.consent) {
      alert('Please accept the consent to continue.');
      return;
    }

    if (!formData.name || !formData.email || !formData.phone) {
      alert('Please fill your name, email and phone.');
      return;
    }

    // Validation: Email
    if (!formData.email.includes('@')) {
      alert('Please enter a valid email address.');
      return;
    }

    // Validation: Phone (Strict 10 digits)
    const phoneRegex = /^\d{10}$/;
    if (!phoneRegex.test(formData.phone)) {
      alert('Please enter a valid 10-digit phone number.');
      return;
    }

    setSubmitting(true);
    const isProposalMode = selectedPlan === 'legacy-growth-mastery' || selectedPlan === 'leadership-legacy-program';

    const payload = {
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      number: Number(formData.phone), // Force as Number type for old backend
      numberStr: formData.phone, // Force as String
      message:
        formData.notes ||
        `Enrolment for programme: ${programme.label} (from Programmes page)`,
      sessionType: programme.label,
      date: '',
      time: '',
      amount: programme.price,
    };

    try {
      if (isProposalMode) {
        // PROPOSAL MODE
        const res = await fetch(`${API_BASE}/api/contact/proposal`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        const result = await res.json();

        if (res.ok) {
          setProposalSent(true);
          setFormData({ name: '', email: '', phone: '', notes: '', consent: false });
        } else {
          alert(result.message || 'Failed to submit proposal.');
        }

      } else {
        // PAYMENT MODE (Existing Logic)
        console.log('[ProgrammeCheckout] Sending payload:', payload);

        const res = await fetch(`${API_BASE}/api/payment/initiate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });

        const result = await res.json().catch(() => null);
        console.log('[ProgrammeCheckout] Response:', res.status, result);

        if (res.ok && result) {
          window.location.href = result.data.instrumentResponse.redirectInfo.url;
        } else {
          alert((result && result.message) || 'Something went wrong. Please try again.');
        }
      }
    } catch (error) {
      console.error('[ProgrammeCheckout] Error:', error);
      alert('Server is not responding. Please try again later.');
    } finally {
      setSubmitting(false);
    }
  };

  const currentProgramme =
    selectedPlan != null ? PROGRAMME_PRICING[selectedPlan] : null;

  const isProposalUI =
    selectedPlan === 'legacy-growth-mastery' ||
    selectedPlan === 'leadership-legacy-program';

  return (
    <div className="bg-exiser-light min-h-screen">
      {/* Header */}
      <div className="bg-exiser-primary text-white py-20 text-center relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10" />
        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <h1 className="text-4xl md:text-5xl font-bold uppercase mb-4 tracking-tight">
            Programmes &amp; Investment
          </h1>
          <p className="text-xl text-purple-200 font-sans max-w-2xl mx-auto">
            Choose your growth path and enroll in the programme that matches
            your next breakthrough.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-16 space-y-10">
        {/* Sales highlight strip */}
        <div className="bg-white border border-purple-100 rounded-2xl p-4 md:p-5 flex flex-col md:flex-row items-center justify-between gap-3 shadow-sm">
          <p className="text-sm text-gray-700 font-sans text-center md:text-left">
            💡{' '}
            <span className="font-bold text-exiser-primary">
              High performers
            </span>{' '}
            invest in specialised programmes, not random motivation.
          </p>
          <p className="text-[11px] uppercase tracking-[0.2em] font-bold text-gray-500 text-center">
            All programmes are conducted live online + guided implementation
          </p>
        </div>



        {/* Row 1: Growth & Manifestation */}
        <div className="grid md:grid-cols-2 gap-8">
          {/* Card 1: Growth Science */}
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-purple-100 flex flex-col hover:-translate-y-2 transition-transform duration-300">
            <div className="bg-purple-50 p-8 border-b border-purple-100 relative">
              <div className="absolute top-4 right-4 bg-exiser-primary text-white text-[11px] font-bold uppercase px-3 py-1 rounded-full tracking-widest">
                Skill + Psychology
              </div>
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center text-exiser-primary shadow-sm mb-6">
                <Brain className="w-8 h-8" />
              </div>
              <h3 className="text-2xl font-bold uppercase text-exiser-primary mb-3">
                Growth Science &amp; Psychology
              </h3>
              <p className="text-gray-600 font-sans leading-relaxed text-sm">
                Designed for ambitious professionals who want science-backed
                tools to upgrade identity, performance and results.
              </p>
            </div>

            <div className="p-8 flex-1 bg-white flex flex-col gap-4">
              {/* Growth Masterycustomer dasboard  */}
              <div className="border border-green-200 rounded-xl p-4 bg-green-50/60">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-sm font-bold text-gray-900">
                    Growth Mastery
                  </p>
                  <span className="text-[11px] font-bold text-exiser-primary bg-white px-3 py-1 rounded-full flex flex-col items-end">
                    <span>₹999</span>
                  </span>
                </div>
                <p className="text-xs text-gray-600 mb-3">
                  A guided inner-work lab to shift self-image from “confused &
                  average” to “clear, confident, growth-focused”.
                </p>
                <div className="flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={() =>
                      handleEnrollClick('identity-transformation-lab')
                    }
                    className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider bg-exiser-primary text-white px-4 py-2.5 rounded-lg hover:bg-purple-900 shadow-md"
                  >
                    Enroll Now
                    <ArrowRight className="w-3 h-3" />
                  </button>
                  <Link
                    to="/identity-transformation-lab"
                    className="text-[11px] font-bold uppercase border-b border-exiser-primary text-exiser-primary flex items-center gap-1"
                  >
                    View Programme Details
                    <ArrowRight className="w-3 h-3" />
                  </Link>
                </div>
              </div>

              {/* Main paid programme highlight */}
              <div className="border border-exiser-primary/30 rounded-xl p-4 bg-purple-50/60">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <p className="text-sm font-bold text-gray-900">
                    Advance Growth Mastery
                  </p>
                  <span className="text-[11px] font-bold text-exiser-primary bg-white px-3 py-1 rounded-full flex flex-col items-end">
                    <span>₹15,000</span>
                  </span>
                </div>
                <p className="text-xs text-gray-600 mb-2">
                  A deep inner-work + strategy programme to permanently shift
                  your growth blueprint.
                </p>
                <p className="text-[10px] text-gray-500 uppercase tracking-[0.18em] mb-3">
                  For working professionals • Entrepreneurs • Leaders
                </p>
                <div className="flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={() =>
                      handleEnrollClick('advance-growth-mastery')
                    }
                    className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider bg-exiser-primary text-white px-4 py-2.5 rounded-lg hover:bg-purple-900 shadow-md"
                  >
                    Enroll Now
                    <ArrowRight className="w-3 h-3" />
                  </button>
                  <Link
                    to="/advance-growth-mastery"
                    className="text-[11px] font-bold uppercase border-b border-exiser-primary text-exiser-primary flex items-center gap-1"
                  >
                    View Programme Details
                    <ArrowRight className="w-3 h-3" />
                  </Link>
                </div>
              </div>

              <p className="text-[11px] text-gray-500 mt-auto">
                Not sure which is right for you?{' '}
                <Link to="/contact" className="font-bold text-exiser-primary hover:underline">
                  Book a clarity call
                </Link>{' '}
                and we’ll guide you to the best track.
              </p>
            </div>
          </div>

          {/* Card 2: Manifestation */}
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-purple-100 flex flex-col hover:-translate-y-2 transition-transform duration-300">
            <div className="bg-yellow-50 p-8 border-b border-yellow-100 relative">
              <div className="absolute top-4 right-4 bg-yellow-400 text-exiser-primary text-[11px] font-bold uppercase px-3 py-1 rounded-full tracking-widest">
                Inner Shift
              </div>
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center text-exiser-accent shadow-sm mb-6">
                <Sparkles className="w-8 h-8 fill-current" />
              </div>
              <h3 className="text-2xl font-bold uppercase text-gray-900 mb-3">
                Manifestation &amp; Visualization Science
              </h3>
              <p className="text-gray-600 font-sans leading-relaxed text-sm">
                Perfect if you’re ready to rewire your subconscious, take
                aligned action and manifest faster.
              </p>
            </div>

            <div className="p-8 flex-1 bg-white flex flex-col gap-6">
              <div className="grid grid-cols-1 gap-4">
                {/* Starter */}
                <div className="border border-yellow-200 rounded-xl p-4 bg-yellow-50/70">
                  <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-yellow-700 mb-1">
                    Daily Practice • HAPPY HOUR
                  </p>
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-sm font-bold text-gray-900">
                      Creative Manifestation Practice
                    </p>
                    <span className="text-[11px] font-bold text-exiser-primary bg-white px-3 py-1 rounded-full flex flex-col items-end">
                      <span>₹1,000</span>
                    </span>
                  </div>
                  <p className="text-xs text-gray-600 mb-3">
                    Daily guided visualisation to align your mind with your
                    goals.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <button
                      type="button"
                      onClick={() =>
                        handleEnrollClick('scientific-visualization-daily')
                      }
                      className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider border border-exiser-primary text-exiser-primary px-4 py-2 rounded-lg hover:bg-exiser-primary hover:text-white transition-colors"
                    >
                      Start Now
                      <ArrowRight className="w-3 h-3" />
                    </button>
                    <Link
                      to="/daily-scientific-visualization"
                      className="text-[11px] font-bold uppercase border-b border-exiser-primary text-exiser-primary flex items-center gap-1"
                    >
                      View Programme Details
                      <ArrowRight className="w-3 h-3" />
                    </Link>
                  </div>
                </div>

                {/* Flagship */}
                <div className="border border-purple-300 rounded-xl p-4 bg-purple-50">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-sm font-bold text-gray-900">
                      Limitless Mind Rewiring Programme
                    </p>
                    <span className="text-[11px] font-bold text-exiser-primary bg-white px-3 py-1 rounded-full flex flex-col items-end">
                      <span>₹4,999</span>
                    </span>
                  </div>
                  <p className="text-[11px] text-purple-700 font-bold mb-1 uppercase tracking-[0.18em]">
                    Most popular for deep inner shift
                  </p>
                  <p className="text-xs text-gray-600 mb-3">
                    A structured rewiring process to break old patterns and
                    install a high-performance, abundant identity.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <button
                      type="button"
                      onClick={() =>
                        handleEnrollClick('limitless-mind-rewiring')
                      }
                      className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider bg-exiser-primary text-white px-4 py-2.5 rounded-lg hover:bg-purple-900 shadow-md"
                    >
                      Enroll Now
                      <ArrowRight className="w-3 h-3" />
                    </button>
                    <Link
                      to="/limitless-mind-rewiring"
                      className="text-[11px] font-bold uppercase border-b border-exiser-primary text-exiser-primary flex items-center gap-1"
                    >
                      View Programme Details
                      <ArrowRight className="w-3 h-3" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Row 2: Student Excellence */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-purple-100 hover:-translate-y-2 transition-transform duration-300 md:max-w-5xl mx-auto w-full">
          <div className="md:flex h-full">
            <div className="md:w-5/12 bg-gray-900 p-8 md:p-12 flex flex-col justify-center text-white relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -mr-16 -mt-16 blur-2xl" />
              <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center text-white shadow-sm mb-6 relative z-10">
                <GraduationCap className="w-8 h-8" />
              </div>
              <h3 className="text-2xl font-bold uppercase mb-4 text-white relative z-10">
                Career Growth and Excellence
              </h3>
              <p className="text-gray-300 font-sans leading-relaxed relative z-10 text-sm">
                For students who want clarity of direction, confidence and high
                performance in studies and life decisions.
              </p>
              <p className="text-[11px] text-gray-400 mt-4 relative z-10 uppercase tracking-[0.18em]">
                Ideal for: Early career Professionals
              </p>
            </div>

            <div className="md:w-7/12 p-8 md:p-12 bg-white flex flex-col justify-center gap-6">
              {/* Student Dream Mastery */}
              <div className="border border-purple-100 rounded-xl p-4 bg-purple-50/40">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-sm font-bold text-gray-900">
                    Career Dream Mastery
                  </p>
                  <span className="text-[11px] font-bold text-exiser-primary bg-white px-3 py-1 rounded-full flex flex-col items-end">
                    <span>₹999</span>
                  </span>
                </div>
                <p className="text-xs text-gray-600 mb-3">
                  Helps students get clarity of dreams, boost focus and create a
                  practical action plan.
                </p>
                <div className="flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={() =>
                      handleEnrollClick('student-dream-mastery')
                    }
                    className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider border border-exiser-primary text-exiser-primary px-4 py-2 rounded-lg hover:bg-exiser-primary hover:text-white transition-colors"
                  >
                    Enroll Now
                    <ArrowRight className="w-3 h-3" />
                  </button>
                  <Link
                    to="/student-dream-mastery"
                    className="text-[11px] font-bold uppercase border-b border-exiser-primary text-exiser-primary flex items-center gap-1"
                  >
                    View Programme Details
                    <ArrowRight className="w-3 h-3" />
                  </Link>
                </div>
              </div>


            </div>
          </div>
        </div>

        {/* Row 3: Exclusive Insurance Programmes (UPDATED) */}
        {/* Row 3: Exclusive Insurance Programmes (REDESIGNED) */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-purple-100 hover:-translate-y-2 transition-transform duration-300 md:max-w-5xl mx-auto w-full">
          <div className="p-8 md:p-12 bg-exiser-primary border-b border-purple-800">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-center gap-6">
                <div className="w-20 h-20 bg-white/10 rounded-2xl flex items-center justify-center text-exiser-accent shadow-lg border border-white/10 rotate-3 backdrop-blur-sm">
                  <Building2 className="w-10 h-10" />
                </div>
                <div>
                  <h3 className="text-3xl font-bold uppercase text-white tracking-tight">
                    Exclusive Programmes For Insurance Professionals
                  </h3>
                  <p className="text-sm text-purple-200 mt-2 max-w-xl leading-relaxed">
                    A premium suite of growth systems specially designed for
                    <span className="font-bold text-exiser-accent"> Life Insurance Advisors, Leaders &amp; Business Associates</span>.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Advisor + Leadership split */}
          <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-purple-100">
            {/* Advisor Category */}
            <div className="p-8 space-y-8">
              <div className="flex items-center gap-4 mb-6">
                <div className="p-3 bg-purple-50 rounded-lg text-exiser-primary">
                  <Briefcase className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-[0.25em] text-gray-400 font-bold">
                    Category 01
                  </p>
                  <p className="text-lg font-bold text-gray-900 uppercase">
                    Advisor Growth
                  </p>
                </div>
              </div>

              {/* 1. Special Growth Mastery */}
              <div className="group border border-gray-100 rounded-xl p-5 hover:shadow-lg hover:border-purple-200 transition-all duration-300 bg-white">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex flex-col items-start gap-1">
                    <span className="inline-flex items-center gap-0.5 bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded text-[9px] border border-gray-200 font-bold uppercase tracking-wider"><Star className="w-2.5 h-2.5 fill-current" /> SILVER</span>
                    <p className="text-[10px] text-exiser-primary uppercase tracking-[0.25em] font-bold">
                      Standard Programme
                    </p>
                  </div>
                  <span className="text-[11px] font-bold bg-purple-50 text-exiser-primary px-3 py-1 rounded-full group-hover:bg-exiser-primary group-hover:text-white transition-colors flex flex-col items-end">
                    <span>₹4,999</span>
                  </span>
                </div>
                <h4 className="text-base font-bold uppercase text-gray-900 mb-2">
                  Special Growth Mastery
                </h4>
                <p className="text-xs text-gray-500 mb-4 leading-relaxed">
                  Foundation growth &amp; performance system for Life Insurance Advisors.
                </p>
                <ul className="space-y-2 text-[11px] text-gray-600 mb-4">
                  <li className="flex items-center gap-2">
                    <Check className="w-3.5 h-3.5 text-green-500" />
                    Growth psychology, performance &amp; mindset
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-3.5 h-3.5 text-green-500" />
                    Sales, communication &amp; rejection handling
                  </li>
                </ul>
                <div className="flex items-center justify-between pt-2 border-t border-gray-50">
                  <Link
                    to="/special-growth-mastery"
                    className="text-[11px] font-bold uppercase text-gray-500 hover:text-exiser-primary transition-colors flex items-center gap-1"
                  >
                    View Details <ArrowRight className="w-3 h-3" />
                  </Link>
                  <button
                    onClick={() => handleEnrollClick('special-growth-mastery')}
                    className="text-[10px] font-bold uppercase tracking-wider bg-gray-900 text-white px-4 py-2 rounded-lg hover:bg-exiser-primary transition-colors shadow-sm"
                  >
                    Enroll Now
                  </button>
                </div>
              </div>

              {/* 2. Elite Growth Mastery */}
              <div className="group border border-purple-100 rounded-xl p-5 hover:shadow-lg hover:border-purple-300 transition-all duration-300 bg-purple-50/30">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex flex-col items-start gap-1">
                    <span className="inline-flex items-center gap-0.5 bg-yellow-100 text-yellow-800 px-1.5 py-0.5 rounded text-[9px] border border-yellow-200 font-bold uppercase tracking-wider"><Crown className="w-2.5 h-2.5 fill-current" /> GOLD</span>
                    <p className="text-[10px] text-exiser-primary uppercase tracking-[0.25em] font-bold">
                      Advance Programme
                    </p>
                  </div>
                  <span className="text-[11px] font-bold bg-white text-exiser-primary border border-purple-100 px-3 py-1 rounded-full shadow-sm flex flex-col items-end">
                    <span>₹24,999</span>
                  </span>
                </div>
                <h4 className="text-base font-bold uppercase text-gray-900 mb-2">
                  Elite Growth Mastery
                </h4>
                <p className="text-xs text-gray-500 mb-4 leading-relaxed">
                  90-day deep growth &amp; performance system for serious advisors.
                </p>
                <div className="flex items-center justify-between pt-2 border-t border-purple-100/50">
                  <Link
                    to="/elite-growth-mastery"
                    className="text-[11px] font-bold uppercase text-gray-500 hover:text-exiser-primary transition-colors flex items-center gap-1"
                  >
                    View Details <ArrowRight className="w-3 h-3" />
                  </Link>
                  <button
                    onClick={() => handleEnrollClick('elite-growth-mastery')}
                    className="text-[10px] font-bold uppercase tracking-wider bg-exiser-primary text-white px-4 py-2 rounded-lg hover:bg-purple-900 transition-colors shadow-md"
                  >
                    Enroll Now
                  </button>
                </div>
              </div>

              {/* 3. Legacy Growth Mastery */}
              <div className="group border border-yellow-200 rounded-xl p-5 hover:shadow-xl hover:border-yellow-400 transition-all duration-300 bg-gradient-to-br from-yellow-50/50 to-white relative overflow-hidden">
                <div className="absolute top-0 right-0 w-20 h-20 bg-yellow-100 rounded-full blur-2xl -mr-10 -mt-10 opacity-50"></div>

                <div className="flex items-center justify-between mb-2 relative z-10">
                  <div className="flex flex-col items-start gap-1">
                    <span className="inline-flex items-center gap-0.5 bg-blue-50 text-blue-800 px-1.5 py-0.5 rounded text-[9px] border border-blue-100 font-bold uppercase tracking-wider"><Gem className="w-2.5 h-2.5 fill-current" /> DIAMOND</span>
                    <p className="text-[10px] text-yellow-700 uppercase tracking-[0.25em] font-bold">
                      VIP Transformation Programme
                    </p>
                  </div>
                  <span className="text-[11px] font-bold bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full flex flex-col items-end">
                    <span>12 Months • ₹99,999</span>
                  </span>
                </div>
                <h4 className="text-base font-bold uppercase text-gray-900 mb-2 relative z-10">
                  Legacy Growth Mastery
                </h4>
                <p className="text-xs text-gray-600 mb-4 leading-relaxed relative z-10">
                  1-year ultimate VIP transformation for advisors who want to become a brand and build a legacy.
                </p>
                <div className="flex items-center justify-between pt-2 border-t border-yellow-100 relative z-10">
                  <Link
                    to="/legacy-growth-mastery"
                    className="text-[11px] font-bold uppercase text-gray-500 hover:text-yellow-700 transition-colors flex items-center gap-1"
                  >
                    View Details <ArrowRight className="w-3 h-3" />
                  </Link>
                  <button
                    onClick={() => handleEnrollClick('legacy-growth-mastery')}
                    className="text-[10px] font-bold uppercase tracking-wider bg-gradient-to-r from-yellow-500 to-yellow-600 text-white px-4 py-2 rounded-lg hover:shadow-lg transition-all shadow-md"
                  >
                    Enroll Now
                  </button>
                </div>
              </div>
              <div className="mt-6 p-4 bg-purple-50/50 rounded-xl border border-purple-100/50">
                <p className="text-[11px] text-gray-500 italic text-center">
                  *Disclaimer: All training and coaching is for educational and professional development purpose only.
                </p>
              </div>
            </div>

            {/* Leadership Category */}
            <div className="p-8 space-y-8 bg-gray-50/50">
              <div className="flex items-center gap-4 mb-6">
                <div className="p-3 bg-purple-50 rounded-lg text-exiser-primary">
                  <Users className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-[0.25em] text-gray-400 font-bold">
                    Category 02
                  </p>
                  <p className="text-lg font-bold text-gray-900 uppercase">
                    Leadership &amp; Management
                  </p>
                </div>
              </div>

              {/* 1. Leadership Growth Mastery */}
              <div className="group border border-gray-100 rounded-xl p-5 hover:shadow-lg hover:border-purple-200 transition-all duration-300 bg-white">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex flex-col items-start gap-1">
                    <span className="inline-flex items-center gap-0.5 bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded text-[9px] border border-gray-200 font-bold uppercase tracking-wider"><Star className="w-2.5 h-2.5 fill-current" /> SILVER</span>
                    <p className="text-[10px] text-exiser-primary uppercase tracking-[0.25em] font-bold">
                      High-Performance Leadership
                    </p>
                  </div>
                  <span className="text-[11px] font-bold bg-purple-50 text-exiser-primary px-3 py-1 rounded-full group-hover:bg-exiser-primary group-hover:text-white transition-colors flex flex-col items-end">
                    <span> ₹4,999</span>
                  </span>
                </div>
                <h4 className="text-base font-bold uppercase text-gray-900 mb-2">
                  Leadership Growth Mastery
                </h4>
                <p className="text-xs text-gray-500 mb-4 leading-relaxed">
                  Complete leadership, recruitment &amp; agency growth system for leaders and managers.
                </p>
                <ul className="space-y-2 text-[11px] text-gray-600 mb-4">
                  <li className="flex items-center gap-2">
                    <Check className="w-3.5 h-3.5 text-green-500" /> Consistent recruitment system
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-3.5 h-3.5 text-green-500" /> Advisor activation &amp; momentum
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-3.5 h-3.5 text-green-500" /> Digital leadership &amp; sales growth
                  </li>
                </ul>
                <div className="flex items-center justify-between pt-2 border-t border-gray-50">
                  <Link
                    to="/leadership-growth-mastery"
                    className="text-[11px] font-bold uppercase text-gray-500 hover:text-exiser-primary transition-colors flex items-center gap-1"
                  >
                    View Details <ArrowRight className="w-3 h-3" />
                  </Link>
                  <button
                    onClick={() => handleEnrollClick('leadership-growth-mastery')}
                    className="text-[10px] font-bold uppercase tracking-wider bg-gray-900 text-white px-4 py-2 rounded-lg hover:bg-exiser-primary transition-colors shadow-sm"
                  >
                    Enroll Now
                  </button>
                </div>
              </div>

              {/* 2. Advance Leadership Transformation */}
              <div className="group border border-purple-100 rounded-xl p-5 hover:shadow-xl hover:border-purple-300 transition-all duration-300 bg-white relative">
                <div className="absolute top-0 right-0 bg-exiser-accent text-exiser-primary text-[9px] font-bold px-2 py-1 uppercase rounded-bl-lg">
                  Best Value
                </div>
                <div className="flex items-center justify-between mb-2 mt-1">
                  <div className="flex flex-col items-start gap-1">
                    <span className="inline-flex items-center gap-0.5 bg-yellow-100 text-yellow-800 px-1.5 py-0.5 rounded text-[9px] border border-yellow-200 font-bold uppercase tracking-wider"><Crown className="w-2.5 h-2.5 fill-current" /> GOLD</span>
                    <p className="text-[10px] text-exiser-primary uppercase tracking-[0.25em] font-bold">
                      Leadership Transformation
                    </p>
                  </div>
                </div>
                <div className="mb-3">
                  <h4 className="text-sm font-bold uppercase text-gray-900">
                    Advance Leadership Transformation Mastery
                  </h4>
                </div>

                <div className="flex items-end gap-2 mb-4">
                  <span className="text-lg font-bold text-exiser-primary">₹24,999</span>
                  <span className="text-[10px] font-bold text-green-600 bg-green-50 px-2 py-0.5 rounded-full mb-1 ml-auto">
                    Limited Offer
                  </span>
                </div>

                <p className="text-xs text-gray-500 mb-4 leading-relaxed">
                  90-day elite system with weekly coaching, audits, recruitment systems, retention &amp; psychology – designed to build legacy leaders.
                </p>
                <div className="flex items-center justify-between pt-2 border-t border-purple-50">
                  <Link
                    to="/advance-leadership-transformation-mastery"
                    className="text-[11px] font-bold uppercase text-gray-500 hover:text-exiser-primary transition-colors flex items-center gap-1"
                  >
                    View Details <ArrowRight className="w-3 h-3" />
                  </Link>
                  <button
                    onClick={() => handleEnrollClick('advance-leadership-transformation-mastery')}
                    className="text-[10px] font-bold uppercase tracking-wider bg-exiser-primary text-white px-4 py-2 rounded-lg hover:bg-purple-900 transition-colors shadow-md"
                  >
                    Enroll Now
                  </button>
                </div>
              </div>

              {/* 3. Leadership Legacy Program */}
              <div className="group border border-yellow-200 rounded-xl p-5 hover:shadow-xl hover:border-yellow-400 transition-all duration-300 bg-gradient-to-br from-yellow-50/50 to-white relative overflow-hidden">
                <div className="absolute top-0 right-0 w-20 h-20 bg-yellow-100 rounded-full blur-2xl -mr-10 -mt-10 opacity-50"></div>

                <div className="flex items-center justify-between mb-2 relative z-10">
                  <div className="flex flex-col items-start gap-1">
                    <span className="inline-flex items-center gap-0.5 bg-blue-50 text-blue-800 px-1.5 py-0.5 rounded text-[9px] border border-blue-100 font-bold uppercase tracking-wider"><Gem className="w-2.5 h-2.5 fill-current" /> DIAMOND</span>
                    <p className="text-[10px] text-yellow-700 uppercase tracking-[0.25em] font-bold">
                      Elite Leadership Programme
                    </p>
                  </div>
                  <span className="text-[11px] font-bold bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full flex flex-col items-end">
                    <span>12 Months • ₹99,999</span>
                  </span>
                </div>
                <h4 className="text-base font-bold uppercase text-gray-900 mb-2 relative z-10">
                  Leadership Legacy Program
                </h4>
                <p className="text-xs text-gray-600 mb-4 leading-relaxed relative z-10">
                  Rebuild identity → Engineer performance → Build wealth → Create legacy.
                </p>
                <div className="flex items-center justify-between pt-2 border-t border-yellow-100 relative z-10">
                  <Link
                    to="/leadership-legacy-program"
                    className="text-[11px] font-bold uppercase text-gray-500 hover:text-yellow-700 transition-colors flex items-center gap-1"
                  >
                    View Details <ArrowRight className="w-3 h-3" />
                  </Link>
                  <button
                    onClick={() => handleEnrollClick('leadership-legacy-program')}
                    className="text-[10px] font-bold uppercase tracking-wider bg-gradient-to-r from-yellow-500 to-yellow-600 text-white px-4 py-2 rounded-lg hover:shadow-lg transition-all shadow-md"
                  >
                    Enroll Now
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="text-center pt-4">
          <p className="text-[11px] text-gray-500 uppercase tracking-[0.22em] mb-2">
            Need help choosing a programme?
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider bg-exiser-primary text-white px-6 py-3 rounded-lg hover:bg-purple-900 shadow-md"
          >
            Book a Clarity Call
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {/* Inline Checkout */}
        {currentProgramme && (
          <div
            id="programme-checkout"
            className="mt-10 max-w-4xl mx-auto bg-white border border-purple-100 rounded-2xl shadow-2xl overflow-hidden"
          >
            <div className="bg-gray-900 text-white px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-exiser-accent flex items-center justify-center text-exiser-primary">
                  <ShoppingCart className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.18em] text-gray-300">
                    {isProposalUI ? 'Request Proposal' : 'Programme Checkout'}
                  </p>
                  <p className="text-sm font-bold">
                    {currentProgramme.label}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setSelectedPlan(null)}
                className="text-gray-300 hover:text-white"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="px-6 py-6 grid md:grid-cols-5 gap-6">
              {/* Summary (Only show if NOT proposal mode) */}
              {!isProposalUI && (
                <div className="md:col-span-2 space-y-3">
                  <h4 className="text-sm font-bold text-gray-900 uppercase tracking-wide">
                    Order Summary
                  </h4>
                  <div className="border border-gray-200 rounded-xl p-4 bg-gray-50 space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium text-gray-800">
                        {currentProgramme.label}
                      </span>
                      <span className="font-bold text-exiser-primary">
                        ₹{currentProgramme.price.toLocaleString('en-IN')}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-xs text-gray-500 pt-2 border-t border-gray-200">
                      <span>GST / Taxes (if applicable)</span>
                      <span>As per invoice</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-sm font-bold text-gray-900">
                    <span>Total Payable</span>
                    <span className="text-exiser-primary">
                      ₹{currentProgramme.price.toLocaleString('en-IN')}
                    </span>
                  </div>
                  <p className="text-[11px] text-gray-500 flex items-center gap-1 mt-1">
                    <ShieldCheck className="w-3 h-3 text-exiser-primary" />{' '}
                    Secure PhonePe payment • Instant confirmation on WhatsApp
                  </p>
                </div>
              )}

              {/* Form (Span full width if proposal mode) */}
              <div className={isProposalUI ? "md:col-span-5" : "md:col-span-3"}>
                {proposalSent ? (
                  <div className="flex flex-col items-center justify-center text-center p-8 bg-green-50 rounded-xl border border-green-200 h-full">
                    <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mb-4">
                      <Check className="w-8 h-8" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Proposal Request Sent!</h3>
                    <p className="text-gray-600 text-sm mb-6 max-w-sm">
                      Thank you for your interest. We have received your details and will connect with you shortly.
                    </p>
                    <button
                      onClick={() => {
                        setProposalSent(false);
                        setSelectedPlan(null);
                      }}
                      className="bg-exiser-primary text-white px-6 py-2 rounded-lg text-sm font-bold uppercase tracking-wider hover:bg-purple-900 transition-colors"
                    >
                      Close
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <label className="text-[10px] font-bold uppercase tracking-wider text-gray-600">
                          Full Name
                        </label>
                        <input
                          type="text"
                          name="name"
                          required
                          value={formData.name}
                          onChange={handleInputChange}
                          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-gray-50 focus:outline-none focus:border-exiser-primary focus:ring-1 focus:ring-exiser-primary"
                          placeholder="Your full name"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-[10px] font-bold uppercase tracking-wider text-gray-600">
                          Email
                        </label>
                        <input
                          type="email"
                          name="email"
                          required
                          value={formData.email}
                          onChange={handleInputChange}
                          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-gray-50 focus:outline-none focus:border-exiser-primary focus:ring-1 focus:ring-exiser-primary"
                          placeholder="you@example.com"
                        />
                      </div>
                    </div>

                    <div className="space-y-1">
                      <label className="text-[10px] font-bold uppercase tracking-wider text-gray-600">
                        Phone
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        required
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-gray-50 focus:outline-none focus:border-exiser-primary focus:ring-1 focus:ring-exiser-primary"
                        placeholder="+91..."
                      />
                    </div>

                    <div className="space-y-1">
                      <label className="text-[10px] font-bold uppercase tracking-wider text-gray-600">
                        Anything you’d like to add? (Optional)
                      </label>
                      <textarea
                        name="notes"
                        rows={3}
                        value={formData.notes}
                        onChange={handleInputChange}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-gray-50 focus:outline-none focus:border-exiser-primary focus:ring-1 focus:ring-exiser-primary resize-none"
                        placeholder="Share your goals, current challenges or expectations from this programme."
                      />
                    </div>

                    <div className="flex items-start gap-2 bg-yellow-50 border border-yellow-100 rounded-lg p-3">
                      <input
                        type="checkbox"
                        name="consent"
                        id="programme-consent"
                        checked={formData.consent}
                        onChange={handleInputChange}
                        className="mt-0.5 w-4 h-4 border-gray-300 text-exiser-primary focus:ring-exiser-primary rounded"
                      />
                      <label
                        htmlFor="programme-consent"
                        className="text-[11px] text-gray-600 leading-snug cursor-pointer"
                      >
                        I agree to be contacted by Exiser on WhatsApp / Email and
                        understand that this is a paid programme. I have read the{' '}
                        <Link
                          to="/refund_policy"
                          className="underline underline-offset-2"
                        >
                          policy
                        </Link>
                        .
                      </label>
                    </div>

                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full bg-exiser-primary text-white font-bold uppercase py-3 rounded-lg text-sm tracking-[0.2em] hover:bg-purple-900 transition-all shadow-lg flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        isProposalUI ? (
                          <>Submit Proposal & Enroll Now</>
                        ) : (
                          <>
                            Pay ₹
                            {currentProgramme.price.toLocaleString('en-IN')} &amp;
                            Enroll Now
                          </>
                        )
                      )}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div >
  );
};

export default ProgrammeCategories;